import type { Clock } from './clock.js';
import { type LocalStore, Collections } from './local-store.js';

/**
 * Local Backup & Restore — full, faithful, portable snapshots of the local
 * store. This is the local-first answer to "is my data safe / can I move it":
 * export the entire device store to one JSON file, restore it anywhere.
 *
 * Snapshots are versioned and checksummed so a corrupted or tampered file is
 * detected before it can overwrite live data.
 */

export const SNAPSHOT_VERSION = 1;
export const SNAPSHOT_KIND = 'life-os.snapshot';

export interface Snapshot {
  readonly kind: typeof SNAPSHOT_KIND;
  readonly version: number;
  readonly exportedAt: string;
  /** FNV-1a checksum over the canonical JSON of `collections`. */
  readonly checksum: string;
  /** collectionName -> [id, record][] */
  readonly collections: Record<string, (readonly [string, unknown])[]>;
}

/** Stable, dependency-free content hash (FNV-1a, 32-bit) for integrity checks. */
function checksum(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** Deterministic serialization of the collections payload for checksumming. */
function canonical(collections: Snapshot['collections']): string {
  const names = Object.keys(collections).sort();
  return JSON.stringify(names.map((n) => [n, collections[n]]));
}

/** Export the whole store as a versioned, checksummed snapshot. */
export async function exportSnapshot(store: LocalStore, clock: Clock): Promise<Snapshot> {
  const collections: Record<string, (readonly [string, unknown])[]> = {};
  for (const name of Object.values(Collections)) {
    const entries = await store.entries<unknown>(name);
    if (entries.length > 0) collections[name] = entries.map(([id, value]) => [id, value]);
  }
  return {
    kind: SNAPSHOT_KIND,
    version: SNAPSHOT_VERSION,
    exportedAt: clock.nowIso(),
    checksum: checksum(canonical(collections)),
    collections,
  };
}

export class InvalidSnapshotError extends Error {
  constructor(detail: string) {
    super(`Invalid snapshot: ${detail}`);
    this.name = 'InvalidSnapshotError';
  }
}

/** Validate a snapshot's shape, version, and checksum. Throws on any mismatch. */
export function validateSnapshot(value: unknown): asserts value is Snapshot {
  if (typeof value !== 'object' || value === null) throw new InvalidSnapshotError('not an object');
  const snap = value as Partial<Snapshot>;
  if (snap.kind !== SNAPSHOT_KIND) throw new InvalidSnapshotError('wrong file kind');
  if (snap.version !== SNAPSHOT_VERSION) throw new InvalidSnapshotError(`unsupported version ${String(snap.version)}`);
  if (typeof snap.collections !== 'object' || snap.collections === null) throw new InvalidSnapshotError('missing collections');
  const expected = checksum(canonical(snap.collections as Snapshot['collections']));
  if (snap.checksum !== expected) throw new InvalidSnapshotError('checksum mismatch (file is corrupt or tampered)');
}

/**
 * Restore a snapshot into the store. Validates integrity first, then writes
 * every record back under its original key. Records are merged (put), never
 * silently dropped; existing untouched collections are preserved.
 */
export async function importSnapshot(store: LocalStore, value: unknown): Promise<{ restored: number }> {
  validateSnapshot(value);
  let restored = 0;
  for (const [name, entries] of Object.entries(value.collections)) {
    for (const [id, record] of entries) {
      await store.put(name, id, record);
      restored += 1;
    }
  }
  return { restored };
}
